import React from 'react';
import { Link } from 'react-router-dom';
import { PARTNERS_SEED } from '../../data/partnersSeed';

function StarRating({ rating }) {
  const fullStars = Math.floor(rating);
  const fraction = Math.round((rating - fullStars) * 10) / 10;

  return (
    <span className="by-bld-stars" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => {
        if (i < fullStars) {
          return (
            <span key={i} className="by-bld-star by-bld-star-full" aria-hidden="true">
              ★
            </span>
          );
        }

        if (i === fullStars && fraction > 0) {
          return (
            <span key={i} className="by-bld-star by-bld-star-partial" aria-hidden="true">
              <span className="by-bld-star-bg">★</span>
              <span className="by-bld-star-fill" style={{ width: `${fraction * 100}%` }}>
                ★
              </span>
            </span>
          );
        }

        return (
          <span key={i} className="by-bld-star by-bld-star-empty" aria-hidden="true">
            ★
          </span>
        );
      })}
    </span>
  );
}

function renderBio(bio, establishedYear) {
  if (!establishedYear) return bio;

  const needle = `since ${establishedYear}`;
  const index = bio.indexOf(needle);
  if (index === -1) return bio;

  return (
    <>
      {bio.slice(0, index)}
      <em>{needle}</em>
      {bio.slice(index + needle.length)}
    </>
  );
}

const DEFAULT_PARTNER = {
  ...PARTNERS_SEED[0],
  locationLabel: 'Austin, TX',
  roleLabel: 'General Contractor · Austin, TX',
};

export default function BuildPartnerCard({ partner, stamp = 'Featured Build Partner' }) {
  const resolved = partner ?? DEFAULT_PARTNER;
  if (!resolved) return null;

  const {
    name,
    roleLabel,
    avatarLetter,
    heroImageUrl,
    bio,
    establishedYear,
    facts = [],
    googleRating,
    googleUrl,
    houzzRating,
    houzzUrl,
    instagramHandle,
    instagramUrl,
    recruitmentNote,
    recruitmentLinkPath = '/interest',
  } = resolved;

  return (
    <div className="by-bld">
      <div className="pic" style={{ backgroundImage: `url('${heroImageUrl}')` }}>
        <span className="stamp">{stamp}</span>
      </div>
      <div className="body">
        <div className="who">
          <span className="av">{avatarLetter}</span>
          <div>
            <b>{name}</b>
            <span>{roleLabel}</span>
          </div>
        </div>
        <blockquote>{renderBio(bio, establishedYear)}</blockquote>
        <div className="facts">
          {facts.map((fact) => (
            <span key={fact}>{fact}</span>
          ))}
        </div>
        <div className="by-bld-reviews">
          {googleRating && googleUrl ? (
            <a className="by-bld-review-pill" href={googleUrl} target="_blank" rel="noopener noreferrer">
              <StarRating rating={googleRating} />
              <span className="by-bld-review-text">
                <b>{googleRating}</b> on Google
              </span>
            </a>
          ) : null}
          {houzzRating && houzzUrl ? (
            <a className="by-bld-review-pill" href={houzzUrl} target="_blank" rel="noopener noreferrer">
              <StarRating rating={houzzRating} />
              <span className="by-bld-review-text">
                <b>{houzzRating}</b> on Houzz
              </span>
            </a>
          ) : null}
          {instagramHandle && instagramUrl ? (
            <a className="by-bld-review-pill" href={instagramUrl} target="_blank" rel="noopener noreferrer">
              <span className="by-bld-ig-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg>
              </span>
              <span className="by-bld-review-text">{instagramHandle}</span>
            </a>
          ) : null}
        </div>
        {recruitmentNote ? (
          <p className="note">
            {recruitmentNote}{' '}
            <Link to={recruitmentLinkPath} className="by-bld-note-link">
              Austin-area contractors, come build with us →
            </Link>
          </p>
        ) : null}
      </div>
    </div>
  );
}
